import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import {assert} from 'Asserts';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch, getStore} from 'redux-stores/Store';
import {
  FulfillmentStatus,
  REQUEST_FULFILLMENT_DEFINITION_SHAPE,
  REQUEST_FULFILLMENT_SHAPE,
  RequestFulfillment,
  RequestFulfillmentDefinition,
} from 'requests/fulfillments/FulfillmentConstants';
import {listFulfillmentsForRequests} from 'requests/fulfillments/FulfillmentReduxActions';
import {fulfillmentMatchesDefinition} from 'requests/fulfillments/FulfillmentUtils';
import {fetchRequestSampleGroups} from 'requests/inputs/InputReduxActions';
import {Request, REQUEST_SHAPE, RequestSchema} from 'requests/RequestConstants';
import * as RequestTaskSchemaReduxActions from 'requests/schemas/RequestTaskSchemaReduxActions';
import {fetchTasksForRequests} from 'requests/tasks/RequestTaskReduxActions';
import {Filter, FILTER_SHAPE} from 'search/SearchConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ModalAnchor from 'ui-library/ModalAnchor';
import Modal from 'ui-library/modals/Modal';
import bufferForUX from 'util/bufferForUX';

import ConfirmReexecuteFulfillmentPane from './ConfirmReexecuteFulfillmentPane';
import {EXECUTION_TYPES, ExecutionType, PANE_KEYS, PANE_ORDER, PaneKey} from './ExecuteRequestFlowConstants';
import {
  closeExecuteRequestFlow,
  goToNextPane,
  goToPrevPane,
  openExecuteRequestFlow,
  setExecutionType,
  setSelectedExecutedFulfillmentDefinitions,
  setSelectedFulfillmentDefinitions,
  setStatusFilter,
} from './ExecuteRequestFlowReduxActions';
import ExecutionPane from './ExecutionPane';
import SelectExecutionTypePane from './SelectExecutionTypePane';
import SelectSamplesPane from './SelectSamplesPane';

export interface BaseExecuteRequestFlowProps {
  requests: Array<Request>;
  requestSchemasByRequestId: {[requestId: string]: RequestSchema};
  onRequestClose: () => void;
  initialFilters: Array<Filter>;
}
export interface ExecuteRequestFlowDispatchProps {
  openExecuteRequestFlow: (args: {excludedPanes: Array<PaneKey>; paneKey: PaneKey | null}) => void;
  closeExecuteRequestFlow: () => void;
  fetchRequestSampleGroups: (requestIds: Array<string>) => Promise<void>;
  fetchTasksForRequests: (requestIds: Array<string>) => Promise<void>;
  fetchRequestTaskSchemas: () => Promise<void>;
  listFulfillmentsForRequests: (requestIds: Array<string>) => Promise<void>;
  goToNextPane: () => void;
  goToPrevPane: () => void;
  setExecutionType: (executionType: ExecutionType) => void;
  setSelectedFulfillmentDefinitions: (fulfillmentDefinitions: Array<RequestFulfillmentDefinition>) => void;
  setSelectedExecutedFulfillmentDefinitions: (
    fulfillmentDefinitions: Array<RequestFulfillmentDefinition>
  ) => void;
  setStatusFilter: (statusFilter: FulfillmentStatus) => void;
}

export interface ExecuteRequestFlowConnectedProps {
  excludedPanes: Array<PaneKey>;
  executionType: ExecutionType | null;
  paneKey: PaneKey | null;
  selectedFulfillmentDefinitions: Array<RequestFulfillmentDefinition> | null;
  selectedExecutedFulfillmentDefinitions: Array<RequestFulfillmentDefinition>;
  statusFilter: FulfillmentStatus | null;
  fulfillmentsByRequestIds: {[requestId: string]: Array<RequestFulfillment>};
}

interface State {
  dataLoaded: boolean;
  hasSelectedExecutedFulfillment: boolean;
}

const BASE_TITLE = 'Execute request';
const BASE_TITLE_PREFIX = `${BASE_TITLE}: `;
const DOUBLE_CLICK_DISABLED_WINDOW = 300;

export class ExecuteRequestFlow extends React.Component<
  BaseExecuteRequestFlowProps & ExecuteRequestFlowConnectedProps & ExecuteRequestFlowDispatchProps,
  State
> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    // Provided by render
    requests: PropTypes.arrayOf(REQUEST_SHAPE.isRequired).isRequired,
    requestSchemasByRequestId: PropTypes.object.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    initialFilters: PropTypes.arrayOf(FILTER_SHAPE.isRequired).isRequired,

    // mapStateToProps
    excludedPanes: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(PANE_KEYS))).isRequired,
    executionType: PropTypes.oneOf(Object.keys(EXECUTION_TYPES)),
    paneKey: PropTypes.oneOf(Object.keys(PANE_KEYS)),
    selectedFulfillmentDefinitions: PropTypes.arrayOf(REQUEST_FULFILLMENT_DEFINITION_SHAPE.isRequired),
    selectedExecutedFulfillmentDefinitions: PropTypes.arrayOf(REQUEST_FULFILLMENT_DEFINITION_SHAPE.isRequired)
      .isRequired,
    statusFilter: PropTypes.oneOf(Object.values(FulfillmentStatus)),
    fulfillmentsByRequestIds: PropTypes.objectOf(PropTypes.arrayOf(REQUEST_FULFILLMENT_SHAPE)),
    openExecuteRequestFlow: PropTypes.func.isRequired,
    closeExecuteRequestFlow: PropTypes.func.isRequired,
    fetchRequestSampleGroups: PropTypes.func.isRequired,
    fetchTasksForRequests: PropTypes.func.isRequired,
    fetchRequestTaskSchemas: PropTypes.func.isRequired,
    listFulfillmentsForRequests: PropTypes.func.isRequired,
    goToNextPane: PropTypes.func.isRequired,
    goToPrevPane: PropTypes.func.isRequired,
    setExecutionType: PropTypes.func.isRequired,
    setSelectedFulfillmentDefinitions: PropTypes.func.isRequired,
    setStatusFilter: PropTypes.func.isRequired,
  };

  override state: State = {
    dataLoaded: false,
    hasSelectedExecutedFulfillment: false,
  };

  override componentDidMount(): void {
    this.props.requests.forEach((request) => {
      assert(request?.id != null, `Expected request ${request} to have a non-null ID`);
    });
    const hasRequestSampleGroupSchema = this._doRequestsHaveSampleGroupSchemas();
    const excludedPanes = hasRequestSampleGroupSchema
      ? [PaneKey.CONFIRM_REEXECUTE]
      : [PaneKey.CONFIRM_REEXECUTE, PaneKey.SELECT_SAMPLES];
    const paneKey = hasRequestSampleGroupSchema ? PaneKey.SELECT_SAMPLES : PaneKey.SELECT_EXECUTION_TYPE;

    if (!hasRequestSampleGroupSchema && this.props.selectedFulfillmentDefinitions == null) {
      this.props.setSelectedFulfillmentDefinitions([]);
    }
    this.props.openExecuteRequestFlow({
      excludedPanes,
      paneKey,
    });

    this._loadData();
  }

  override componentDidUpdate(
    _prevProps: BaseExecuteRequestFlowProps &
      ExecuteRequestFlowConnectedProps &
      ExecuteRequestFlowDispatchProps,
    prevState: State
  ): void {
    if (prevState.hasSelectedExecutedFulfillment !== this.state.hasSelectedExecutedFulfillment) {
      const excludedPanes = this._doRequestsHaveSampleGroupSchemas() ? [] : [PaneKey.SELECT_SAMPLES];
      if (!this.state.hasSelectedExecutedFulfillment) {
        excludedPanes.push(PaneKey.CONFIRM_REEXECUTE);
      }
      this.props.openExecuteRequestFlow({
        excludedPanes,
        paneKey: this.props.paneKey,
      });
    }
  }

  async _loadData(): Promise<void> {
    // eslint-disable-next-line no-restricted-syntax
    const requestIds = _.pluck(this.props.requests, 'id');

    await bufferForUX(
      Promise.all([
        this.props.fetchRequestSampleGroups(requestIds),
        this.props.listFulfillmentsForRequests(requestIds),
        this.props.fetchRequestTaskSchemas(),
        this.props.fetchTasksForRequests(requestIds),
      ]),
      800
    );
    this.setState({dataLoaded: true});
  }

  _doRequestsHaveSampleGroupSchemas(): boolean {
    const allHaveSchema = this.props.requests.every(
      (request) => this.props.requestSchemasByRequestId[request.id].sampleGroupSchema != null
    );
    const noneHaveSchema = this.props.requests.every(
      (request) => this.props.requestSchemasByRequestId[request.id].sampleGroupSchema == null
    );
    assert(
      allHaveSchema || noneHaveSchema,
      'Either all requests should have a sample group schema, or none should'
    );

    return allHaveSchema;
  }

  _handleCloseModal = (): void => {
    this.props.onRequestClose();
    this.props.closeExecuteRequestFlow();
  };

  _getRequestIdsWithSelectedFulfillments(): Set<string> {
    if (this.props.selectedFulfillmentDefinitions == null) {
      return new Set();
    }
    // eslint-disable-next-line no-restricted-syntax
    const requestIds = new Set(_.pluck(this.props.selectedFulfillmentDefinitions, 'requestId'));
    for (const request of this.props.requests) {
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      if (this.props.requestSchemasByRequestId[request.id].sampleGroupSchema === null) {
        requestIds.add(request.id);
      }
    }
    return requestIds;
  }

  _getSelectedExecutedFulfillmentDefinitions(
    fulfillmentDefinitions: Array<RequestFulfillmentDefinition>
  ): Array<RequestFulfillmentDefinition> {
    const fulfillmentIsExecuted = (fulfillment: RequestFulfillment): boolean =>
      fulfillment.status === FulfillmentStatus.COMPLETED ||
      fulfillment.status === FulfillmentStatus.IN_PROGRESS;

    return fulfillmentDefinitions.filter((fulfillmentDefinition: RequestFulfillmentDefinition) => {
      const matchingFulfillmentIdx = this.props.fulfillmentsByRequestIds[
        fulfillmentDefinition.requestId
      ].findIndex(
        (fulfillment: RequestFulfillment) =>
          fulfillmentMatchesDefinition(fulfillment, fulfillmentDefinition) &&
          fulfillmentIsExecuted(fulfillment)
      );
      return matchingFulfillmentIdx >= 0;
    });
  }

  _handleSelectedFulfillmentDefinitions = (
    fulfillmentDefinitions: Array<RequestFulfillmentDefinition>
  ): void => {
    this.props.setSelectedFulfillmentDefinitions(fulfillmentDefinitions);
    const selectedExecutedFulfillmentDefinitions =
      this._getSelectedExecutedFulfillmentDefinitions(fulfillmentDefinitions);
    this.props.setSelectedExecutedFulfillmentDefinitions(selectedExecutedFulfillmentDefinitions);
    this.setState({
      hasSelectedExecutedFulfillment: selectedExecutedFulfillmentDefinitions.length > 0,
    });
  };

  _renderLoadingPane(): JSX.Element {
    return (
      <Modal large={true} onRequestClose={this._handleCloseModal} title={BASE_TITLE}>
        <CenteredHorizontalThrobber />
      </Modal>
    );
  }

  _renderPane(): JSX.Element {
    const firstPane = PANE_ORDER.filter((paneKey) => !this.props.excludedPanes.includes(paneKey))[0];
    const selectedRequestIds = this._getRequestIdsWithSelectedFulfillments();

    switch (this.props.paneKey) {
      case PANE_KEYS.SELECT_SAMPLES:
        return (
          <SelectSamplesPane
            titlePrefix={BASE_TITLE_PREFIX}
            requests={this.props.requests}
            requestSchemasByRequestId={this.props.requestSchemasByRequestId}
            statusFilter={this.props.statusFilter}
            initialFilters={this.props.initialFilters}
            onGoToNextPane={this.props.goToNextPane}
            onRequestClose={this._handleCloseModal}
            onSetSelectedFulfillmentDefinitions={this._handleSelectedFulfillmentDefinitions}
            onSetStatusFilter={this.props.setStatusFilter}
          />
        );
      case PANE_KEYS.CONFIRM_REEXECUTE:
        return (
          <ConfirmReexecuteFulfillmentPane
            requests={this.props.requests}
            requestSchemasByRequestId={this.props.requestSchemasByRequestId}
            fulfillmentDefinitions={this.props.selectedExecutedFulfillmentDefinitions}
            statusFilter={this.props.statusFilter}
            onGoToNextPane={this.props.goToNextPane}
            onGoToPrevPane={this.props.goToPrevPane}
            onRequestClose={this._handleCloseModal}
            onSetStatusFilter={this.props.setStatusFilter}
          />
        );
      case PANE_KEYS.SELECT_EXECUTION_TYPE:
        return (
          <SelectExecutionTypePane
            requestIds={Array.from(selectedRequestIds)}
            selectedExecutionType={this.props.executionType}
            isFirstPane={firstPane === PANE_KEYS.SELECT_EXECUTION_TYPE}
            onGoToNextPane={this.props.goToNextPane}
            onGoToPrevPane={this.props.goToPrevPane}
            onRequestClose={this._handleCloseModal}
            onSetExecutionType={this.props.setExecutionType}
          />
        );
      case PANE_KEYS.EXECUTE:
        return (
          <ExecutionPane
            executionType={this.props.executionType!}
            requests={this.props.requests.filter((request) => selectedRequestIds.has(request.id))}
            requestSchemasByRequestId={this.props.requestSchemasByRequestId}
            onRequestClose={this._handleCloseModal}
          />
        );
      default:
        throw new Error(`Unexpected pane ${this.props.paneKey}`);
    }
  }

  override render(): JSX.Element {
    if (!this.state.dataLoaded || this.props.paneKey == null) {
      return this._renderLoadingPane();
    }

    return <ModalAnchor open={true}>{this._renderPane()}</ModalAnchor>;
  }
}

function mapStateToProps(state: BenchlingState): ExecuteRequestFlowConnectedProps {
  const {
    excludedPanes,
    executionType,
    paneKey,
    selectedFulfillmentDefinitions,
    selectedExecutedFulfillmentDefinitions,
    statusFilter,
  } = state.ui.requests.executeRequestFlow;
  const {fulfillmentIdsByRequestId, fulfillmentsById} = state.models.requests.fulfillments;
  const fulfillmentsByRequestIds: {[requestId: string]: Array<RequestFulfillment>} = {};
  Object.keys(fulfillmentIdsByRequestId).forEach((requestId) => {
    fulfillmentsByRequestIds[requestId] = fulfillmentIdsByRequestId[requestId].map(
      (fulfillmentId) => fulfillmentsById[fulfillmentId]
    );
  });

  return {
    excludedPanes,
    executionType,
    paneKey,
    selectedFulfillmentDefinitions,
    selectedExecutedFulfillmentDefinitions,
    statusFilter,
    fulfillmentsByRequestIds,
  };
}

const debouncedNextPane = debounce(
  () => {
    getStore().dispatch(goToNextPane());
  },
  DOUBLE_CLICK_DISABLED_WINDOW,
  {isImmediate: true}
);

const debouncedPrevPane = debounce(
  () => {
    getStore().dispatch(goToPrevPane());
  },
  DOUBLE_CLICK_DISABLED_WINDOW,
  {isImmediate: true}
);

export function mapDispatchToProps(dispatch: BenchlingDispatch): ExecuteRequestFlowDispatchProps {
  return {
    closeExecuteRequestFlow: () => {
      dispatch(closeExecuteRequestFlow());
    },
    fetchRequestSampleGroups: async (requestIds: Array<string>) => {
      await dispatch(fetchRequestSampleGroups(requestIds));
    },
    fetchRequestTaskSchemas: async () => {
      await dispatch(RequestTaskSchemaReduxActions.fetchAll());
    },
    fetchTasksForRequests: async (requestIds: Array<string>) => {
      await dispatch(fetchTasksForRequests(requestIds));
    },
    goToNextPane: () => {
      debouncedNextPane();
    },
    goToPrevPane: () => {
      debouncedPrevPane();
    },
    listFulfillmentsForRequests: async (requestIds: Array<string>) => {
      await dispatch(listFulfillmentsForRequests(requestIds));
    },
    openExecuteRequestFlow: (args: {excludedPanes: Array<PaneKey>; paneKey: PaneKey | null}) => {
      dispatch(openExecuteRequestFlow(args));
    },
    setExecutionType: (executionType: ExecutionType) => {
      dispatch(setExecutionType(executionType));
    },
    setSelectedFulfillmentDefinitions: (fulfillmentDefinitions: Array<RequestFulfillmentDefinition>) => {
      dispatch(setSelectedFulfillmentDefinitions(fulfillmentDefinitions));
    },
    setSelectedExecutedFulfillmentDefinitions: (
      fulfillmentDefinitions: Array<RequestFulfillmentDefinition>
    ) => {
      dispatch(setSelectedExecutedFulfillmentDefinitions(fulfillmentDefinitions));
    },
    setStatusFilter: (statusFilter: FulfillmentStatus) => {
      dispatch(setStatusFilter(statusFilter));
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ExecuteRequestFlow);
